import React from "react";
import type { DataType } from "../types";
import type { DataTypeConfig } from "../dataTypeConfig";
import { DropdownMultiSelect } from "./DropdownMultiSelect";
import { DropdownMultiSelectNoSelectAll } from "./DropdownMultiSelectNoSelectAll";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

interface Props {
  dataType: DataType;
  config: DataTypeConfig;
  filtersLoading?: boolean;

  // Demand state
  groupBy: string;
  setGroupBy: (v: string) => void;
  groupBySelector: string[];
  setGroupBySelector: (v: string[]) => void;
  demandVariableType: string[];
  setDemandVariableType: (v: string[]) => void;

  generatorType: string[];
  setGeneratorType: (v: string[]) => void;
  generatorOwnership: string[];
  setGeneratorOwnership: (v: string[]) => void;
  selectedGenerators: string[];
  setSelectedGenerators: (v: string[]) => void;
  generationVariableType: string[];
  setGenerationVariableType: (v: string[]) => void;

  unit: string;
  setUnit: (v: string) => void;
}

export function DataBreakdownSection({
  dataType,
  config,
  filtersLoading = false,
  groupBy,
  setGroupBy,
  groupBySelector,
  setGroupBySelector,
  demandVariableType,
  setDemandVariableType,
  generatorType,
  setGeneratorType,
  generatorOwnership,
  setGeneratorOwnership,
  selectedGenerators,
  setSelectedGenerators,
  generationVariableType,
  setGenerationVariableType,
  unit,
  setUnit,
}: Props) {
  const handleGroupByChange = (newGroupBy: string) => {
    setGroupBy(newGroupBy);
    setGroupBySelector([]);
  };

  const handleDemandVariableToggle = (value: string) => {
    setDemandVariableType(
      demandVariableType.includes(value)
        ? demandVariableType.filter((v) => v !== value)
        : [...demandVariableType, value],
    );
  };

  if (!config) return null;

  return (
    <div
      style={{ border: "0px", backgroundColor: "rgba(250, 250, 250, 1)" }}
      className="dd-section-card transition-all duration-300"
    >
      {/* ── Header ── */}
      <div className="dd-section-header">
        <h2 className="dd-section-title">2. Data Breakdown</h2>
        <span className="dd-badge">{dataType}</span>
      </div>

      {/* ── Loader ── */}
      {filtersLoading && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0" }}>
          <DotLottieReact
            src="https://lottie.host/e5d43299-8a12-4fd2-9cd9-17750f9f56c5/lZjt4YNRaX.lottie"
            loop
            autoplay
            style={{ width: 48, height: 48 }}
          />
          <span style={{ fontSize: 13, color: "#9ca3af" }}>Loading filter options…</span>
        </div>
      )}

      {/* ── Demand fields ── */}
      {!filtersLoading && config.type === "demand" && (
        <div className="dd-fields-row animate-in fade-in duration-300">
          {/* Group By */}
          <div className="dd-field-group">
            <span className="dd-field-label">Group By</span>
            <DropdownMultiSelectNoSelectAll
              options={config.groupByOptions}
              value={groupBy ? [groupBy] : []}
              onChange={(selected) => {
                const next = selected[0];
                if (next) handleGroupByChange(next);
              }}
              placeholder="Select Group By"
              singleSelect
            />
          </div>

          {/* Group By Selector */}
          {config.groupBySelectorOptions(groupBy).length > 0 && (
            <div className="dd-field-group">
              <span className="dd-field-label">Group By Selector</span>
              <DropdownMultiSelect
                options={config.groupBySelectorOptions(groupBy)}
                value={groupBySelector}
                onChange={setGroupBySelector}
                placeholder="Select Group By Selector"
              />
            </div>
          )}

          {/* Variable Type */}
          {config.variableTypeOptions.length > 0 && (
            <div className="dd-field-group">
              <span className="dd-field-label">Variable Type</span>
              <div className="dd-checkbox-group">
                {config.variableTypeOptions.map((opt) => {
                  const checked = demandVariableType.includes(opt.value);
                  return (
                    <label
                      key={opt.value}
                      className={`dd-checkbox-pill${checked ? " dd-checkbox-pill--checked" : ""}`}
                    >
                      <input
                        type="checkbox"
                        className="dd-checkbox-input"
                        checked={checked}
                        onChange={() => handleDemandVariableToggle(opt.value)}
                      />
                      <span className="dd-pill-box">
                        {checked && (
                          <svg className="dd-pill-check" viewBox="0 0 12 12" fill="none">
                            <polyline
                              points="2,6.5 5,9.5 10,3"
                              stroke="#fff"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Unit */}
          <div className="dd-field-group">
            <span className="dd-field-label">Unit</span>
            <div className="dd-checkbox-group">
              {["MW", "MWh"].map((opt) => {
                const checked = unit === opt;
                return (
                  <label
                    key={opt}
                    className={`dd-checkbox-pill${checked ? " dd-checkbox-pill--checked" : ""}`}
                  >
                    <input
                      type="radio"
                      className="dd-checkbox-input"
                      checked={checked}
                      onChange={() => setUnit(opt)}
                    />
                    <span className="dd-pill-box">
                      {checked && (
                        <svg className="dd-pill-check" viewBox="0 0 12 12" fill="none">
                          <polyline
                            points="2,6.5 5,9.5 10,3"
                            stroke="#fff"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                    {opt}
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Generation fields ── */}
      {!filtersLoading && config.type === "generation" && (
        <div className="dd-fields-row animate-in fade-in duration-300">
          {/* Generator Type */}
          <div className="dd-field-group">
            <span className="dd-field-label">Generator Type (Source)</span>
            <DropdownMultiSelect
              options={config.generatorTypeOptions}
              value={generatorType}
              onChange={(selected) => {
                setGeneratorType(selected);
                // Reset downstream selections when type changes
                setGeneratorOwnership([]);
                setSelectedGenerators([]);
              }}
              placeholder="Select Generator Type"
            />
          </div>

          {/* Generator Ownership — filtered by selected types */}
          <div className="dd-field-group">
            <span className="dd-field-label">Generator Ownership</span>
            <DropdownMultiSelect
              options={config.ownershipOptionsFor(generatorType)}
              value={generatorOwnership}
              onChange={(selected) => {
                setGeneratorOwnership(selected);
                // Reset generators when ownership changes
                setSelectedGenerators([]);
              }}
              placeholder="Select Ownership"
            />
          </div>

          {/* List of Generators — filtered by type and ownership */}
          <div className="dd-field-group">
            <span className="dd-field-label">List Of Generators</span>
            <DropdownMultiSelect
              options={config.generatorOptionsFor(generatorType, generatorOwnership)}
              value={selectedGenerators}
              onChange={setSelectedGenerators}
              placeholder="Select Generators"
            />
          </div>

          {/* Variable Type */}
          {config.variableTypeOptions.length > 0 && (
            <div className="dd-field-group">
              <span className="dd-field-label">Variable Type</span>
              <div className="dd-checkbox-group">
                {config.variableTypeOptions.map((opt) => {
                  const checked = generationVariableType.includes(opt.value);
                  return (
                    <label
                      key={opt.value}
                      className={`dd-checkbox-pill${checked ? " dd-checkbox-pill--checked" : ""}`}
                    >
                      <input
                        type="checkbox"
                        className="dd-checkbox-input"
                        checked={checked}
                        onChange={() => {
                          setGenerationVariableType(
                            checked
                              ? generationVariableType.filter((v) => v !== opt.value)
                              : [...generationVariableType, opt.value],
                          );
                        }}
                      />
                      <span className="dd-pill-box">
                        {checked && (
                          <svg className="dd-pill-check" viewBox="0 0 12 12" fill="none">
                            <polyline
                              points="2,6.5 5,9.5 10,3"
                              stroke="#fff"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Unit */}
          <div className="dd-field-group">
            <span className="dd-field-label">Unit</span>
            <div className="dd-checkbox-group">
              {["MW", "MWh"].map((opt) => {
                const checked = unit === opt;
                return (
                  <label
                    key={opt}
                    className={`dd-checkbox-pill${checked ? " dd-checkbox-pill--checked" : ""}`}
                  >
                    <input
                      type="radio"
                      className="dd-checkbox-input"
                      checked={checked}
                      onChange={() => setUnit(opt)}
                    />
                    <span className="dd-pill-box">
                      {checked && (
                        <svg className="dd-pill-check" viewBox="0 0 12 12" fill="none">
                          <polyline
                            points="2,6.5 5,9.5 10,3"
                            stroke="#fff"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                    {opt}
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
